import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import {
  SOLVER_RETURN_KEY, SOLVER_SESSION_KEY, parseReturn, parseSession, readStored, sessionStore, writeStored,
  type KeptSolutions, type SolverReturn, type SolverSession,
} from '@/views/planning/simulation-session';

/**
 * The solver page's simulation, kept for the tab (views/planning/simulation-session.ts), and the way
 * back to it: a lead opens a record, the record's page shows « Retour au solveur », and the solver
 * page comes back as the person left it.
 */
export const useSolverSessionStore = defineStore('solverSession', () => {
  const session = ref<SolverSession | null>(readStored(sessionStore(), SOLVER_SESSION_KEY, parseSession));
  const back = ref<SolverReturn | null>(readStored(sessionStore(), SOLVER_RETURN_KEY, parseReturn));
  /** The schedules found, for the way back: in memory only (simulation-session.ts says why). */
  const solutions = shallowRef<KeptSolutions | null>(null);

  function save(next: SolverSession | null) {
    session.value = next;
    writeStored(sessionStore(), SOLVER_SESSION_KEY, next);
  }

  function keepSolutions(next: KeptSolutions | null) {
    solutions.value = next;
  }

  /** A lead opens a record: its page offers the way back. */
  function leaveFor(path: string) {
    back.value = { path };
    writeStored(sessionStore(), SOLVER_RETURN_KEY, back.value);
  }

  /** The way back belongs to the page the lead opened: going anywhere else drops it. */
  function arrivedAt(path: string) {
    if (back.value && back.value.path !== path) forgetReturn();
  }

  function forgetReturn() {
    back.value = null;
    writeStored(sessionStore(), SOLVER_RETURN_KEY, null);
  }

  /** The simulation is over (its schedule applied): the next visit starts afresh. */
  function clear() {
    save(null);
    keepSolutions(null);
    forgetReturn();
  }

  return { session, back, solutions, save, keepSolutions, leaveFor, arrivedAt, forgetReturn, clear };
});
